/**
 * Typed wrappers around `/batches*`. Backed by `useApi`.
 *
 * The returned object is memoized on `[api]` so consumers can safely
 * use it as a dependency in `useCallback`/`useEffect` without spinning
 * the effect on every render. Without this, a parent that calls
 * `useEffect(() => { void api.get(...); }, [api])` re-fetches on every
 * render, which (for `GET /batches/{id}`) re-mints a presigned image
 * URL each time and turns `<img src={imageUrl}>` into a refresh loop.
 */

import { useMemo } from 'react';
import { useApi } from '../api';
import type { ApiResult } from '../auth/api-client';
import type { CaptureBatch, CardIdentity, CardRecord, BatchTransitionOutcome } from '../shared/types';

export interface BatchListResponse {
  items: CaptureBatch[];
}

export function useBatchesApi() {
  const api = useApi();
  return useMemo(
    () => ({
      list: (view?: 'queue' | 'dismissed'): Promise<ApiResult<BatchListResponse>> =>
        api.request<BatchListResponse>(view === 'dismissed' ? '/batches?view=dismissed' : '/batches', {
          method: 'GET',
        }),
      get: (batchId: string): Promise<ApiResult<CaptureBatch>> =>
        // batchId is `{deviceId}#{ISO timestamp}` — the `#` is the URL
        // fragment separator and would be stripped by the browser if we
        // interpolated it raw, so every batchId path segment must go
        // through encodeURIComponent before it lands in the fetch URL.
        api.request<CaptureBatch>(`/batches/${encodeURIComponent(batchId)}`, { method: 'GET' }),
      confirm: (
        batchId: string,
        imageId: string,
      ): Promise<ApiResult<{ card: CardRecord; correctionPersisted: boolean }>> =>
        api.request<{ card: CardRecord; correctionPersisted: boolean }>(
          `/batches/${encodeURIComponent(batchId)}/images/${encodeURIComponent(imageId)}/confirm`,
          { method: 'POST', body: {} },
        ),
      correct: (
        batchId: string,
        imageId: string,
        identity: CardIdentity,
      ): Promise<ApiResult<{ card: CardRecord; correctionPersisted: boolean }>> =>
        api.request<{ card: CardRecord; correctionPersisted: boolean }>(
          `/batches/${encodeURIComponent(batchId)}/images/${encodeURIComponent(imageId)}/correct`,
          { method: 'POST', body: { identity } },
        ),
      reject: (batchId: string, options?: { deleteCard?: boolean }): Promise<ApiResult<void>> =>
        // Reject (hard delete) the batch. Idempotent on the server
        // — repeated rejects of the same batch return 204 just like
        // the first call. When `deleteCard` is set, the server also
        // deletes the Card_Record this batch produced (dev/testing
        // cleanup); the default preserves the card (physical-first).
        api.request<void>(
          `/batches/${encodeURIComponent(batchId)}${
            options?.deleteCard === true ? '?deleteCard=true' : ''
          }`,
          { method: 'DELETE' },
        ),
      dismiss: (batchId: string): Promise<ApiResult<{ batch: CaptureBatch }>> =>
        // Non-destructive: flips the batch to DISMISSED, hiding it from the
        // default review queue without deleting any image, card, or placement.
        api.request<{ batch: CaptureBatch }>(`/batches/${encodeURIComponent(batchId)}/dismiss`, {
          method: 'POST',
          body: {},
        }),
      restore: (batchId: string): Promise<ApiResult<{ batch: CaptureBatch }>> =>
        // Inverse of dismiss: returns a DISMISSED batch to its prior review state.
        api.request<{ batch: CaptureBatch }>(`/batches/${encodeURIComponent(batchId)}/restore`, {
          method: 'POST',
          body: {},
        }),
      bulkDismiss: (batchIds: string[]): Promise<ApiResult<{ outcomes: BatchTransitionOutcome[] }>> =>
        // Bulk dismiss returns one outcome per requested id (partial success).
        api.request<{ outcomes: BatchTransitionOutcome[] }>('/batches/dismiss', {
          method: 'POST',
          body: { batchIds },
        }),
      bulkRestore: (batchIds: string[]): Promise<ApiResult<{ outcomes: BatchTransitionOutcome[] }>> =>
        // Bulk restore returns one outcome per requested id (partial success).
        api.request<{ outcomes: BatchTransitionOutcome[] }>('/batches/restore', {
          method: 'POST',
          body: { batchIds },
        }),
    }),
    [api],
  );
}
